import { type FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Send } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  Button,
  Chip,
  CircularLoadingButton,
  typographyVariants,
} from '@mint-lib/tw-components';
import { StaticDateSpan } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';
import { format, parseISO } from 'date-fns';

import useSendReminderMutation from '../api/mutations/useSendReminderMutation.js';
import {
  getMapLabelByGroupStatus,
  mapChipPropsByGroupStatus,
} from '../constants/shared';
import type { LogicalRowType } from '../types/shared.js';
import EntityGroup from './EntityGroup.jsx';

type Props = LogicalRowType & {
  approvalProcessId: string;
  reminderSendingAllowed: boolean;
};

const LogicalRow: FC<Props> = ({
  items,
  status,
  resolvedAt,
  resolvedBy,
  approvalProcessId,
  reminderSendingAllowed,
}) => {
  const { t } = useTranslation('@mint-lib/approval-flow-circuit_v2');
  const { mutateAsync, isPending } = useSendReminderMutation();

  const mapLabelByGroupStatus = getMapLabelByGroupStatus(t);
  const chipProps = status ? mapChipPropsByGroupStatus[status] : null;
  const showStatusChip = status && status !== 'draft';
  const resolvedAtDate = resolvedAt ? parseISO(resolvedAt) : null;
  const formattedTime = resolvedAtDate ? format(resolvedAtDate, 'HH:mm') : null;
  const showReminderButton = reminderSendingAllowed && status === 'pending';
  const showResolvedBy =
    !!resolvedBy && (items.length > 1 || items.some((item) => item.isRole));

  const handleRemind = async () => {
    try {
      await mutateAsync(approvalProcessId);
    } catch {
      enqueueSnackbar({
        variant: 'error',
        title: t('Reminder was not sent.'),
      });
    }
  };

  return (
    <div className="grid gap-3">
      <div className="flex items-start gap-6">
        <div className="grow">
          <EntityGroup items={items} maxVisibleChipsLimit={3} />
        </div>
        {showStatusChip && (
          <Chip {...chipProps}>{mapLabelByGroupStatus[status]}</Chip>
        )}
      </div>
      <div
        className={cn('wrap flex gap-1', {
          hidden: !resolvedAt && !showReminderButton,
        })}
      >
        {resolvedAt && (
          <span
            className={typographyVariants({
              variant: 'caption1',
              color: 'secondary',
            })}
          >
            <StaticDateSpan date={resolvedAt} />
            {' - '}
            {formattedTime}
          </span>
        )}
        {showResolvedBy && (
          <span
            className={typographyVariants({
              variant: 'caption1',
              color: 'secondary',
            })}
          >
            {t('by {{user}}', { user: resolvedBy })}
          </span>
        )}
        {showReminderButton && (
          <Button
            data-dd-action-name="Send a reminder"
            color="primary"
            variant="plain"
            onClick={handleRemind}
            disabled={isPending}
          >
            {isPending ? (
              <CircularLoadingButton size="small" variant="outlined" />
            ) : (
              <Send />
            )}
            {t('Send a reminder')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default LogicalRow;
